import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Platform } from '@ionic/angular';
import { UserDeviceService } from '../user-device/user-device.service';
import { NavigationService } from '../../navigation/navigation.service';

/**
 * Device Route Guard Service
 * Restricts access to certain pages when running on iOS devices (iPad, iPhone)
 * Only allows access to: login, survey-selection, survey-language-pick, and survey-questionnaire
 */
@Injectable({
  providedIn: 'root'
})
export class DeviceRouteGuardService implements CanActivate {

  // Allowed pages for iOS devices
  private readonly ALLOWED_PATHS = [
    '/login',
    '/survey-selection',
    '/tabs/survey-selection',
    '/survey-langauge-pick',
    '/tabs/survey-language-pick',
    '/survey-questionnaire',
    '/tabs/surveys/questionnaire',
    '/redirect' // Allow redirect page for proper navigation
  ];

  constructor(
    private platform: Platform,
    private deviceService: UserDeviceService,
    private navService: NavigationService,
    private router: Router
  ) {}

  /**
   * Determines if the current route can be activated based on device type
   * @param route Activated Route Snapshot
   * @returns Promise<boolean | UrlTree>
   */
  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    // Check if device information is available
    const userDevice = this.deviceService.getUserDevice();
    if (!userDevice || !userDevice.os) {
      // Device info not available yet, allow all routes
      return true;
    }

    // Check if running on iOS device (iPad, iPhone)
    const isIOSDevice = this.isIOSDevice();

    if (!isIOSDevice) {
      // Not an iOS device, allow all routes
      return true;
    }

    // iOS device detected, check if the requested path is allowed
    const requestedPath = this.getRequestedPath(route);
    const isAllowedPath = this.isPathAllowed(requestedPath);

    if (isAllowedPath) {
      return true;
    }

    // Path not allowed on iOS device, redirect to survey-selection
    console.log(`DeviceRouteGuard: Blocking access to ${requestedPath} on iOS device. Redirecting to survey-selection.`);
    return this.navService.navigateRoot('/tabs/survey-selection');
  }

  /**
   * Checks if the current device is an iOS device (iPad or iPhone)
   * @returns boolean
   */
  private isIOSDevice(): boolean {
    // Check device service
    const userDevice = this.deviceService.getUserDevice();
    if (userDevice && userDevice.os) {
      const os = userDevice.os.toLowerCase();
      return os === 'ios';
    }

    // Fallback: check platform
    return this.platform.is('ios');
  }

  /**
   * Gets the requested path from the route
   * @param route Activated Route Snapshot
   * @returns string
   */
  private getRequestedPath(route: ActivatedRouteSnapshot): string {
    let path = '';

    // Build path from route segments
    if (route.url && route.url.length > 0) {
      path = '/' + route.url.map(segment => segment.path).join('/');
    }

    // Handle root path
    if (path === '' || path === '/') {
      path = '/tabs/surveys'; // Default redirect path
    }

    return path;
  }

  /**
   * Checks if the given path is allowed for iOS devices
   * @param path string
   * @returns boolean
   */
  private isPathAllowed(path: string): boolean {
    // Check exact matches first
    if (this.ALLOWED_PATHS.includes(path)) {
      return true;
    }

    // Check for parameterized routes (e.g., /survey-langauge-pick/123)
    for (const allowedPath of this.ALLOWED_PATHS) {
      if (this.matchesParameterizedRoute(path, allowedPath)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Checks if a path matches a parameterized route pattern
   * @param path string
   * @param pattern string
   * @returns boolean
   */
  private matchesParameterizedRoute(path: string, pattern: string): boolean {
    // Convert pattern to regex
    const regexPattern = pattern.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(path);
  }
}
